import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

import EmployerHeaderNav from '../../components/navigation/EmployerHeaderNav';
import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import employerService from '../../utils/employerService';
import ActivityFilters from './components/ActivityFilters';
import ActivityTimeline from './components/ActivityTimeline';

const ITEMS_PER_PAGE = 12;

const EmployerActivitiesPage = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    types: [],
    startDate: null,
    endDate: null,
    search: '',
  });

  // Activity types definition
  const activityTypesList = useMemo(() => [
    { value: 'application_received', label: 'Applications', icon: 'FileText' },
    { value: 'job_posted', label: 'Job Postings', icon: 'Briefcase' },
    { value: 'message_sent', label: 'Messages', icon: 'MessageSquare' },
    { value: 'interview_scheduled', label: 'Interviews', icon: 'Calendar' },
    { value: 'contract_started', label: 'Contracts Started', icon: 'UserCheck' },
    { value: 'contract_ended', label: 'Contracts Ended', icon: 'UserX' },
    { value: 'candidate_viewed', label: 'Candidate Views', icon: 'Eye' },
    { value: 'job_status_changed', label: 'Job Status Changes', icon: 'RefreshCw' },
    { value: 'system_notification', label: 'System Notifications', icon: 'Bell' }
  ], []);

  const fetchAllActivities = useCallback(async () => {
    if (!userProfile?.id) return;
    
    try {
      setLoading(true);
      setError('');

      const result = await employerService.getAllActivities(userProfile.id);
      if (result.success) {
        setActivities(result.data || []);
        setFilteredActivities(result.data || []);
      } else {
        setError(result.error || 'Failed to load activities');
      }
    } catch (err) {
      setError('Failed to load activities. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  }, [userProfile?.id]);

  useEffect(() => {
    if (user?.id && userProfile?.role === 'employer') {
      fetchAllActivities();
    }
  }, [user?.id, userProfile?.role, fetchAllActivities]);

  // Apply filters to activities
  const applyFilters = useCallback(() => {
    if (!activities.length) {
      setFilteredActivities([]);
      return;
    }

    let result = [...activities];

    // Filter by activity type
    if (filters.types.length > 0) {
      result = result.filter(activity => filters.types.includes(activity.activity_type));
    }

    // Filter by date range
    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      result = result.filter(activity => {
        const activityDate = new Date(activity.activity_date);
        return activityDate >= startDate;
      });
    }

    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999); // End of the day
      result = result.filter(activity => {
        const activityDate = new Date(activity.activity_date);
        return activityDate <= endDate;
      });
    }

    // Filter by search term
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      result = result.filter(activity => 
        activity.description?.toLowerCase().includes(searchTerm) ||
        (activity.metadata?.job_title?.toLowerCase().includes(searchTerm)) ||
        (activity.metadata?.candidate_name?.toLowerCase().includes(searchTerm)) ||
        (activity.metadata?.applicant_name?.toLowerCase().includes(searchTerm)) ||
        (activity.metadata?.va_name?.toLowerCase().includes(searchTerm))
      );
    }

    setFilteredActivities(result);
  }, [filters, activities]);

  useEffect(() => {
    applyFilters();
    setCurrentPage(1);
  }, [applyFilters]);

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      types: [],
      startDate: null,
      endDate: null,
      search: '',
    });
    setCurrentPage(1);
  }, []);

  const handleActivityClick = useCallback((activity) => {
    // Navigate based on activity type
    switch (activity.activity_type) {
      case 'application_received':
        navigate(`/employer-dashboard?tab=applications`);
        break;
      case 'job_posted':
        navigate(`/employer-dashboard?tab=jobs`);
        break;
      case 'message_sent':
        navigate('/messages');
        break;
      case 'interview_scheduled':
        navigate('/interview-management');
        break;
      case 'contract_started':
        navigate('/employer-dashboard?tab=hired');
        break;
      case 'contract_ended':
        navigate('/employer-dashboard?tab=hired');
        break;
      default:
        navigate('/employer-dashboard');
        break;
    }
  }, [navigate]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredActivities.length / ITEMS_PER_PAGE);
  const paginatedActivities = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredActivities.slice(startIndex, endIndex);
  }, [filteredActivities, currentPage]);

  // Custom breadcrumbs if needed for specific scenarios
  const customBreadcrumbs = useMemo(() => {
    return [
      {
        label: 'Dashboard',
        path: '/employer-dashboard',
        icon: 'LayoutDashboard',
        isActive: false
      },
      {
        label: 'Activity History',
        path: '/employer-activities',
        icon: 'Clock',
        isActive: true
      }
    ];
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <BreadcrumbNavigation customBreadcrumbs={customBreadcrumbs} />
      <EmployerHeaderNav />
      
      
      <main className="pt-8 pb-20 md:pb-8">
        <div className="content-container">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <button
                    onClick={() => navigate('/employer-dashboard')}
                    className="text-text-secondary hover:text-primary transition-colors"
                  >
                    <Icon name="ArrowLeft" size={20} />
                  </button>
                  <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">
                    Activity History
                  </h1>
                </div>
                <p className="text-text-secondary">
                  Track all your hiring activities and interactions with virtual assistants
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button 
                  variant="outline" 
                  iconName="RefreshCw" 
                  onClick={fetchAllActivities}
                  isLoading={loading}
                >
                  Refresh
                </Button>
                <Button 
                  variant="primary" 
                  iconName="Filter" 
                  onClick={() => document.getElementById('filters-section').scrollIntoView({ behavior: 'smooth' })}
                >
                  Filter Activities
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Sidebar with Filters */}
            <div className="lg:col-span-3" id="filters-section">
              <ActivityFilters 
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                activityTypes={activityTypesList}
              />
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-9">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                  <span className="ml-4 text-text-secondary">Loading activities...</span>
                </div>
              ) : error ? (
                <div className="text-center py-16">
                  <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-text-primary mb-2">Error Loading Activities</h3>
                  <p className="text-text-secondary mb-4">{error}</p>
                  <Button variant="primary" onClick={fetchAllActivities}>
                    Try Again
                  </Button>
                </div>
              ) : filteredActivities.length === 0 ? (
                <div className="text-center py-16 bg-surface rounded-lg border border-border">
                  <Icon name="Inbox" size={48} className="text-text-secondary mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-text-primary mb-2">No Activities Found</h3>
                  <p className="text-text-secondary mb-4">
                    {activities.length === 0 
                      ? "You don't have any activities yet. Start by posting jobs or reviewing applications."
                      : "No activities match your current filters. Try adjusting your filters or clearing them."}
                  </p>
                  {activities.length > 0 && (
                    <Button variant="outline" onClick={handleClearFilters}>
                      Clear Filters
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <ActivityTimeline 
                    activities={paginatedActivities}
                    onActivityClick={handleActivityClick}
                  />
                  
                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="mt-8 flex items-center justify-between">
                      <div className="text-text-secondary text-sm">
                        Page {currentPage} of {totalPages} ({filteredActivities.length} activities)
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          iconName="ChevronLeft"
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </Button>
                        
                        <div className="flex items-center space-x-1 px-2">
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`w-8 h-8 rounded transition-colors ${
                                currentPage === page
                                  ? 'bg-primary text-white'
                                  : 'text-text-secondary hover:bg-surface'
                              }`}
                            >
                              {page}
                            </button>
                          ))}
                        </div>
                        
                        <Button
                          variant="outline"
                          iconName="ChevronRight"
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>
</div>
  );
};

export default EmployerActivitiesPage;


